'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { categoryStatsData } from '@/data/categories';
import { Users, UserCheck, Globe, ShieldCheck } from 'lucide-react';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

const renderIcon = (iconName: string) => {
  const props = { size: 20, color: '#B08D57', strokeWidth: 1.5 };
  switch (iconName) {
    case 'users': return <Users {...props} />;
    case 'worker': return <UserCheck {...props} />;
    case 'globe': return <Globe {...props} />;
    case 'badge': return <ShieldCheck {...props} />;
    default: return <ShieldCheck {...props} />;
  }
};

export const StatsBar: React.FC = () => {
  const barRef = useRef<HTMLDivElement | null>(null);
  const numberRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(
    () => {
      if (!barRef.current) return;

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: barRef.current,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });

      timeline.fromTo(
        iconRefs.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.5, stagger: 0.12, ease: 'back.out(2.2)' }
      );

      categoryStatsData.forEach((stat, index) => {
        const numElement = numberRefs.current[index];
        if (!numElement) return;

        const counter = { val: 0 };
        timeline.to(
          counter,
          {
            val: stat.value,
            duration: 1.6,
            ease: 'power2.out',
            onUpdate: () => {
              numElement.textContent = Math.floor(counter.val).toLocaleString();
            },
          },
          index === 0 ? '-=0.2' : '-=1.4'
        );
      });
    },
    { scope: barRef }
  );

  return (
    <div
      ref={barRef}
      className="w-full grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#1C1B19]/[0.07] border-t border-b border-[#1C1B19]/[0.07] my-10 sm:my-16"
    >
      {categoryStatsData.map((stat, index) => (
        <div key={stat.id} className="flex items-center justify-center gap-3 px-3 py-7">
          <div
            ref={(el) => { iconRefs.current[index] = el; }}
            className="shrink-0 w-11 h-11 rounded-full border border-[#B08D57]/30 bg-[#FAF6ED] flex items-center justify-center"
          >
            {renderIcon(stat.iconName)}
          </div>

          <div className="flex flex-col text-left">
            <div className="text-xl xs:text-2xl font-extrabold text-[#1F1F1F] leading-tight">
              <span ref={(el) => { numberRefs.current[index] = el; }}>0</span>
              <span>{stat.suffix}</span>
            </div>
            <span className="text-[11px] xs:text-[12px] tracking-wide uppercase text-[#9A9384] font-medium">
              {stat.label}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};